import { useCallback, useMemo } from 'react';
import { useNotifications } from '@/stores/useNotificationStore';
import { iemService } from '@/services/iem';
import { extractErrorMessage } from '../workflowUtils';

export interface UseReservedAssetsOptions {
  externalReservedAssetIds: string[];
  /** Reserved asset IDs from the database cart */
  cartReservedAssetIds: string[];
}

export const useReservedAssets = ({
  externalReservedAssetIds,
  cartReservedAssetIds,
}: UseReservedAssetsOptions) => {
  const createReservedAssetSet = useCallback(() => {
    const reserved = new Set<string>(externalReservedAssetIds);
    cartReservedAssetIds.forEach((id) => reserved.add(id));
    return reserved;
  }, [cartReservedAssetIds, externalReservedAssetIds]);

  const reservedAssetIds = useMemo(() => {
    return [...cartReservedAssetIds];
  }, [cartReservedAssetIds]);

  return {
    createReservedAssetSet,
    reservedAssetIds,
  };
};

export interface AssetAllocatorCart {
  addToCart: (assetIds: string[], ieKitId?: string) => Promise<boolean>;
}

export interface AssetAllocatorOptions {
  dbCart: AssetAllocatorCart;
  createReservedAssetSet: () => Set<string>;
  refreshAvailability?: () => void;
}

export interface AllocateAssetsParams {
  catalogId: string | null | undefined;
  itemName: string;
  quantity: number;
  sizeLabel?: string | null;
  ieKitId?: string | null;
  locationId?: string | null;
}

export const useAssetAllocator = ({
  dbCart,
  createReservedAssetSet,
  refreshAvailability,
}: AssetAllocatorOptions) => {
  const notifications = useNotifications();

  const allocateAssets = useCallback(
    async (params: AllocateAssetsParams): Promise<boolean> => {
      let count = params.quantity;
      if (count <= 0) {
        return false;
      }

      const catalogIdToCheck = params.catalogId?.trim();
      if (!catalogIdToCheck) {
        notifications.warning(
          'Unable to add item',
          'This item is missing a catalog ID, so we cannot verify availability.',
        );
        return false;
      }

      try {
        const itemLocationId = params.locationId ?? undefined;
        const availability = await iemService.checkAvailabilityByCatalogIds([
          { catalogId: catalogIdToCheck, qty: count },
        ], itemLocationId);
        const match = availability.results.find((entry) => entry.catalogId === catalogIdToCheck);
        const onHand = Math.max(match?.on_hand ?? 0, 0);

        if (onHand === 0) {
          notifications.warning(
            'No available assets',
            params.sizeLabel
              ? `No available inventory found for ${params.itemName} (${params.sizeLabel}).`
              : `No available inventory found for ${params.itemName}.`,
          );
          refreshAvailability?.();
          return false;
        }

        if (onHand < count) {
          notifications.warning(
            'Limited inventory',
            params.sizeLabel
              ? `Only ${onHand} available for ${params.itemName} (${params.sizeLabel}). Adding all available.`
              : `Only ${onHand} available for ${params.itemName}. Adding all available.`,
          );
          count = onHand;
        }

        // Use the plural (actions-api) variant so that assets linked to a
        // parent catalog are correctly fanned out to child catalog entries
        // (e.g. a "non-sized" child whose stock lives on the parent).
        const candidateAssets = await iemService.getAvailableAssetsByCatalogIds({
          catalogIds: [catalogIdToCheck],
          limit: Math.max(count * 2, 20),
          locationId: itemLocationId,
        });

        if (!candidateAssets || candidateAssets.length === 0) {
          notifications.warning(
            'No available assets',
            params.sizeLabel
              ? `No available inventory found for ${params.itemName} (${params.sizeLabel}).`
              : `No available inventory found for ${params.itemName}.`,
          );
          refreshAvailability?.();
          return false;
        }

        const reservedAssetIds = createReservedAssetSet();
        const availableAssets = candidateAssets.filter((asset) => !reservedAssetIds.has(asset.id));

        if (availableAssets.length === 0) {
          notifications.warning(
            'No available assets',
            params.sizeLabel
              ? `No available inventory found for ${params.itemName} (${params.sizeLabel}).`
              : `No available inventory found for ${params.itemName}.`,
          );
          refreshAvailability?.();
          return false;
        }

        const assignableAssets = availableAssets.slice(0, count);
        if (assignableAssets.length < count) {
          notifications.warning(
            'Limited inventory',
            `Only ${assignableAssets.length} asset${assignableAssets.length === 1 ? '' : 's'} available for ${params.itemName}${params.sizeLabel ? ` (${params.sizeLabel})` : ''}.`,
          );
        }

        const selectedIds = assignableAssets.map((asset) => asset.id);

        const success = await dbCart.addToCart(selectedIds, params.ieKitId ?? undefined);

        if (!success) {
          refreshAvailability?.();
        }
        return success;
      } catch (error) {
        const message = extractErrorMessage(error, 'Unable to load available assets.');
        notifications.error('Failed to add asset', message);
        return false;
      }
    },
    [createReservedAssetSet, dbCart, notifications, refreshAvailability],
  );

  return { allocateAssets };
};
